/**
 * EP（映画ランド）/web/** 接口：浏览器直连，不经过 DP 后端（DEV-15088）
 * - 不走 request.js：不需要带 DP 的 Token，也不希望弹出 Element 的全局错误提示
 * - 返回统一为 {code:200,msg,data}；data 为空时后端可能直接不返回 data 这个 key，这里统一用 fallback 兜底
 */

const EP_API_PROD = 'https://ep-api.eigaland.com/api';
const EP_API_DEV = 'https://ep-api-dev.eigaland.com/api';

/**
 * EP 接口的 Base URL
 * 构建产物在 dev / uat / prod 共用一份（CI 都是 npm run build），无法在构建期区分，
 * 所以优先读 VITE_APP_EP_API，没有配置时按当前域名判断：本地或带 dev / uat 的域名走 EP dev，其余走 EP prod。
 */
export function getEpApiBase() {
  const fromEnv = import.meta.env.VITE_APP_EP_API;
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  const host = window.location.hostname;
  const isNonProd = host === 'localhost'
    || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)
    || /(^|[.-])(dev|uat|stg|staging)([.-]|$)/.test(host);
  return isNonProd ? EP_API_DEV : EP_API_PROD;
}

// 通用请求：非 200 时抛错，data 缺失时返回 fallback
async function epRequest(path, { method = 'POST', body, params, signal } = {}, fallback = null) {
  let url = `${getEpApiBase()}${path}`;
  if (params) url += `?${new URLSearchParams(params).toString()}`;
  const res = await fetch(url, {
    method,
    signal,
    headers: method === 'POST' ? { 'Content-Type': 'application/json' } : undefined,
    body: method === 'POST' ? JSON.stringify(body ?? {}) : undefined,
  });
  if (!res.ok) throw new Error(`EP ${path} HTTP ${res.status}`);
  const json = await res.json();
  if (json?.code !== 200) throw new Error(json?.msg || `EP ${path} code ${json?.code}`);
  return json.data ?? fallback;
}

// 作品详情：id / name / duration(分) / openDate("yyyy/MM/dd") / distributor / thumbnail …
export function fetchEpMovieDetail(id, options = {}) {
  return epRequest('/web/movie/detail', { method: 'GET', params: { id }, ...options }, null);
}

// 有场次的日期：prefectureList 为空时后端默认只查东京都，调用方必须传展开后的都道府県
export function fetchEpScheduleDates({ movieId, prefectureList }, options = {}) {
  return epRequest(
    '/web/schedule/queryDatesList',
    { body: { type: 'movie', movieId, prefectureList }, ...options },
    [],
  );
}

// 某一天的场次（按影院分组）；一次只能查一天
export function fetchEpMovieSchedules({ movieId, showDate, prefectureList, cityList = [] }, options = {}) {
  return epRequest(
    '/web/schedule/queryMovieSchedules',
    { body: { movieId, showDate, prefectureList, cityList }, ...options },
    [],
  );
}

// 区域树：地域(level1) → 都道府県(level2) → 市区町村(level3)
export function fetchEpAreas(options = {}) {
  return epRequest('/web/cinema/areas', { body: {}, ...options }, []);
}

// 影院列表：id / name / prefecture / city / lat / lon …
export function fetchEpCinemaList(options = {}) {
  return epRequest('/web/cinema/list', { body: {}, ...options }, []);
}
